import React, { useDeferredValue, useEffect, useMemo, useState } from "react";
import {
  Box,
  MenuItem,
  Select,
  Switch,
  FormControlLabel,
  Autocomplete,
  TextField,
  Typography,
  Chip,
  Divider,
  LinearProgress,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorIcon from "@mui/icons-material/Error";
import { format } from "date-fns";
import { observer } from "mobx-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import moment from "moment";
import { useNavigate } from "react-router-dom";

import GenericInfiniteTable, {
  ColumnDef,
} from "../../../molecules/PatientRxTables/GenericInfiniteTable";
import {
  PrescribedTherapyOptionDto,
  RxDtoProcessingStatusEnum,
  RxTableDto,
  RxValidationBulkRowDto,
} from "../../../api-client";
import useStore from "../../../helpers/useStore";
import { RegistryContext } from "../../../stores/Registry/registry.provider";
import { registryKeys } from "../../../stores/Registry/registry.keys";
import { useApi } from "../../../api/ApiProvider";
import { useLocationId, useUserAccounts } from "../../../stores/User/User.hooks";
import {
  useRxTypes,
  useTarifGroups,
} from "../../../stores/MasterTables/therapies.queries";
import { PatientsContext } from "../../../stores/Patients/patients.provider";
import { UserContext } from "../../../stores/User/User.provider";
import { getStatusColor } from "../../PatientOverview/RxInfo/RxForms/statusColor";
import FlatCard from "../../../atoms/FlatCard";
import { rxValidationRunKey, useRxValidationMode } from "../hooks/useRxValidationMode";
import { useLocations } from "../../../stores/User/user.queries";
import notificationStore from "../../../stores/Notification/notificationStore";
import { usePrescribedTherapyOptions } from "../../../stores/MasterTables/masterTables.queries";
import {
  filterPrescribedTherapyOptions,
  prescribedTherapyInputValue,
} from "../../../helpers/prescribedTherapyMatch";
import { therapyRxAmountList } from "../../../helpers/therapyRxLabel";

// Soft red palette for validation mode — a light, non-alarming red used only
// for the active state, count emphasis and the streaming progress bar.
const VALIDATION_COLORS = {
  bgHover: "#FDECEA",
  border: "#D32F2F",
  text: "#C62828",
  pillContent: "#bb3232",
  /** Green badge for a clean run (0 Fehlern) — reads as "all good". */
  ok: "#2E7D32",
  /** Pill fill while a run is in progress — vivid, reads as "working". */
  bgRunning: "#F7D9D6",
  /** Pill fill once finished but still on — muted, reads as "on, at rest". */
  bgRest: "#FBEDEB",
  /** Hover fill for the filled/active pill — a touch stronger so hover darkens. */
  bgActiveHover: "#F2C9C5",
} as const;

// How many validation rows to add per render window step (grown on scroll).
const VALIDATION_PAGE_SIZE = 50;

/**
 * Registry tab for Rezepte (Rx).
 *
 * Columns: Rezeptnummer · Name · Erster Termin · Anzahl Therapien ·
 *          Rezeptdatum · Versicherungsgesellschaft · Tarifgruppe · Status · Typ · Konto
 *
 * Filters:
 *   - Bearbeitungsstatus
 *   - Gelöschte anzeigen
 *   - Tarifgruppe
 *   - Rx-Typ
 *   - Konto (Account)
 *   - Heilmittel (prescribed term — matches Rxs having at least one such line)
 *
 * Default sort: creation date descending.
 */
const RegistryRxTab: React.FC = observer(() => {
  const RegistryStore = useStore(RegistryContext);
  const PatientsStore = useStore(PatientsContext);
  const UserStore = useStore(UserContext);
  // Rezeptprüfung is a chief-only feature (UI-gated, matching the app's
  // convention of restricting features by role on the client).
  const isChief = Boolean(
    UserStore.user?.roles?.some((role: { id?: string }) => role.id === "chief"),
  );
  const {
    rxFilters: filters,
    setRxFilters,
    rxSelectedIndex: selectedIndex,
    setRxSelectedIndex: setSelectedIndex,
  } = RegistryStore;

  const locationId = useLocationId();
  const navigate = useNavigate();
  const { rx: rxApi } = useApi();
  const { data: tarifGroups } = useTarifGroups();
  const { data: rxTypes } = useRxTypes();
  const { data: locations } = useLocations();
  const accounts = useUserAccounts();
  const { data: prescribedTherapyOptions } = usePrescribedTherapyOptions();

  const tarifGroupOptions = useMemo<Array<{ id: string; name: string }>>(
    () =>
      (tarifGroups || []).map((tg: any) => ({
        id: tg.id,
        name: tg.name ?? tg.id,
      })),
    [tarifGroups],
  );

  // The selected term as an option object — Autocomplete needs the option, the
  // filter stores only the id (that is what the backend matches on).
  const selectedPrescribedTherapy = useMemo(
    () =>
      (prescribedTherapyOptions ?? []).find((o) => o.id === filters.prescribedTherapyId) ??
      null,
    [prescribedTherapyOptions, filters.prescribedTherapyId],
  );

  const rxTypeOptions = useMemo<Array<{ id: string; text: string }>>(
    () =>
      (rxTypes || []).map((rt: any) => ({
        id: rt.id,
        text: rt.text ?? rt.name ?? rt.id,
      })),
    [rxTypes],
  );

  // ---- Effective filters --------------------------------------------------
  const effectiveFilters = useMemo(
    () => ({ ...filters, location: filters.location ?? locationId }),
    [filters, locationId],
  );

  // ---- Infinite query ------------------------------------------------------
  const {
    data,
    isFetchingNextPage,
    isLoading,
    hasNextPage: hasMoreData,
    fetchNextPage,
  } = useInfiniteQuery<RxTableDto[], Error>({
    queryKey: registryKeys.rxs.filtered(effectiveFilters),
    queryFn: async ({ pageParam = 1 }) =>
      rxApi.getFilteredAllRxs({
        ...(effectiveFilters as any),
        page: pageParam as number,
        limit: effectiveFilters.limit,
      }),
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length >= (effectiveFilters.limit ?? 50) ? allPages.length + 1 : undefined,
    initialPageParam: 1,
    staleTime: moment.duration(10, "minutes").asMilliseconds(),
    enabled: !!locationId,
    meta: { errorMessage: "Rezeptliste konnte nicht geladen werden" },
  });

  const rxs: RxTableDto[] = data?.pages.flat() ?? [];

  // ---- Validation mode ----------------------------------------------------
  const validation = useRxValidationMode();
  const validationMode = RegistryStore.validationMode;
  // Defer the value that decides WHICH heavy table renders. When the mode
  // flips, React keeps showing the previous table and renders the new one in a
  // concurrent (interruptible) pass, so the toggle no longer blocks the main
  // thread. Everything else (toolbar, pill, captions) keeps using the immediate
  // `validationMode` so it reacts instantly. `isSwitchingView` is true only
  // while the deferred render is catching up — used to show a spinner overlay.
  const deferredValidationMode = useDeferredValue(validationMode);
  const isSwitchingView = deferredValidationMode !== validationMode;
  const isValidationLoading = validation.status === "running";

  // The run is scoped server-side to a single Standort (`validationLocationId`),
  // defaulting to the user's current location. The Standort dropdown lets the
  // user inspect a *different* location without changing their account location:
  // selecting one starts a new, server-scoped run (deduped/replayed per location
  // by the backend). The streamed result set therefore already contains only the
  // selected location's rows — no client-side filtering is needed.
  const validationLocationId = RegistryStore.validationLocationId;
  const filteredValidationRows = validation.rows;

  // Render rows in a growing window (grown on scroll) instead of all at once,
  // so a large result set never freezes the UI. Reset only when the underlying
  // dataset changes (location filter switch) — NOT when merely toggling the view
  // on/off, so re-entering validation mode preserves how far the user scrolled.
  const [validationRenderLimit, setValidationRenderLimit] = useState(VALIDATION_PAGE_SIZE);
  useEffect(() => {
    setValidationRenderLimit(VALIDATION_PAGE_SIZE);
  }, [validationLocationId]);
  const windowedValidationRows = useMemo(
    () => filteredValidationRows.slice(0, validationRenderLimit),
    [filteredValidationRows, validationRenderLimit],
  );

  const toggleValidationMode = () => {
    if (RegistryStore.validationMode) {
      // Close the view — keep any in-flight run going in the background.
      RegistryStore.setValidationMode(false);
      if (validation.status === "running") {
        notificationStore.showMessage("Rezeptprüfung läuft im Hintergrund…", "info");
      }
      return;
    }
    RegistryStore.setValidationMode(true);
    // Running -> show live; done/error -> show stored results / error state.
    // Reuse what is on screen only when it answers the SAME question: the
    // toolbar filters are part of the run, so re-entering after changing one
    // has to re-check rather than re-display the previous run's numbers
    // (MD-184). The first run defaults to the user's current location (the
    // dropdown lets them switch Standort without changing their account).
    const initialLocation = RegistryStore.validationLocationId ?? locationId;
    const runKey = rxValidationRunKey(initialLocation, filters);
    const nothingToShow = validation.status === "idle" && validation.rows.length === 0;
    if (nothingToShow || validation.runKey !== runKey) {
      RegistryStore.setValidationLocationId(initialLocation);
      void validation.start({ locationId: initialLocation, filters });
    }
  };

  // ---- Row click ----------------------------------------------------------
  // Unified row click handler: both tables open the Rx in the same page.
  const handleRowClick = (row: RxTableDto | RxValidationBulkRowDto) => {
    const patientId = (row.patient as any)?.id as number | undefined;
    if (!patientId || !row.id) return;
    PatientsStore.setSelectedPatientId(patientId);
    PatientsStore.setSelectedRxId(row.id ?? null);
    navigate("/patient");
  };

  // ---- Validation columns -------------------------------------------------
  const validationColumns: ColumnDef<RxValidationBulkRowDto>[] = useMemo(
    () => [
      {
        key: "issues",
        header: "Fehler",
        render: (r) => {
          const issues = r.issues ?? [];
          return (
            <Chip
              size="small"
              icon={
                <ErrorIcon
                  sx={{ color: `${VALIDATION_COLORS.border} !important` }}
                />
              }
              label={issues.length}
              sx={{
                color: VALIDATION_COLORS.text,
                borderColor: VALIDATION_COLORS.border,
              }}
              variant="outlined"
            />
          );
        },
      },
      { key: "rxNumber", header: "Rezeptnummer", render: (r) => r.rxNumber },
      {
        key: "date",
        header: "Rezeptdatum",
        render: (r) => (r.date ? format(new Date(r.date), "dd.MM.yyyy") : "-"),
      },
      {
        key: "patientName",
        header: "Name (Nachname, Vorname)",
        render: (r) =>
          `${(r.patient as any)?.lastName ?? "-"}, ${(r.patient as any)?.firstName ?? "-"}`,
      },
      {
        key: "firstAppointmentDate",
        header: "Erster Termin",
        render: (r) =>
          r.firstAppointmentDate
            ? format(new Date(r.firstAppointmentDate), "dd.MM.yyyy")
            : "-",
      },
      {
        key: "tarifGroup",
        header: "Tarifgruppe",
        render: (r) =>
          (r as any).tarifGroup?.name ??
          ((r as any).therapyRxs?.[0] as any)?.therapy?.tarifGroup?.name ??
          "-",
      },
      {
        key: "processingStatus",
        header: "Status",
        render: (r) => r.processingStatus ?? "-",
      },
      { key: "rxtype", header: "Typ", render: (r) => (r.rxtype as any)?.text ?? "-" },
      {
        key: "account",
        header: "Konto",
        render: (r) => (r as any).account?.name ?? "-",
      },
    ],
    [],
  );

  // ---- Columns -------------------------------------------------------------
  const columns: ColumnDef<RxTableDto>[] = useMemo(
    () => [
      {
        key: "rxNumber",
        header: "Rezeptnummer",
        render: (r) => r.rxNumber,
        sortable: true,
      },
      {
        key: "patientName",
        header: "Name (Nachname, Vorname)",
        render: (r) =>
          `${(r.patient as any)?.lastName ?? "-"}, ${(r.patient as any)?.firstName ?? "-"}`,
        sortable: true,
      },
      {
        key: "firstAppointmentDate",
        header: "Erster Termin",
        render: (r) =>
          r.firstAppointmentDate
            ? format(new Date(r.firstAppointmentDate), "dd.MM.yyyy")
            : "-",
        sortable: true,
      },
      {
        key: "therapyRxs",
        header: "Anzahl Therapien",
        render: (r) => therapyRxAmountList(r.therapyRxs),
      },
      {
        key: "date",
        header: "Rezeptdatum",
        render: (r) => (r.date ? format(new Date(r.date), "dd.MM.yyyy") : "-"),
        sortable: true,
      },
      {
        key: "payer",
        header: "Versicherungsgesellschaft",
        render: (r) => r.payer?.name ?? "-",
      },
      {
        key: "tarifGroup",
        header: "Tarifgruppe",
        // Returned by backend on rx.tarifGroup once extension lands. Best-effort
        // until then: pick from any therapyRx.therapy.tarifGroup that we have.
        render: (r) =>
          (r as any).tarifGroup?.name ??
          (r.therapyRxs?.[0] as any)?.therapy?.tarifGroup?.name ??
          "-",
      },
      {
        key: "processingStatus",
        header: "Status",
        render: (r) => r.processingStatus ?? "-",
      },
      {
        key: "rxtype",
        header: "Typ",
        render: (r) => r.rxtype?.text ?? "-",
      },
      {
        key: "account",
        header: "Konto",
        render: (r) => (r as any).account?.name ?? "-",
      },
    ],
    [],
  );

  // ---- Sort change --------------------------------------------------------
  /**
   * Sorts by one column at a time.
   *
   * Merging the clicked column into the existing sort read as reasonable but
   * never reordered anything: the default `createdAt: "DESC"` stayed first in
   * the object, and the backend walks `Object.entries(sortBy)` in insertion
   * order. Since createdAt is effectively unique per Rx, every column the user
   * clicked landed as a tiebreak on a key that never ties — the control moved,
   * the list did not. The chosen column therefore has to be the only key.
   *
   * Cycle per column: ASC → DESC → back to the default (creation date desc).
   */
  const handleSortChange = (columnKey: string) => {
    setRxFilters((prev) => {
      const currentSort = (prev.sortBy as Record<string, "ASC" | "DESC"> | undefined) || {};
      const currentDir = currentSort[columnKey];
      const newDir =
        currentDir === "ASC" ? "DESC" : currentDir === "DESC" ? undefined : "ASC";
      return {
        sortBy: (newDir ? { [columnKey]: newDir } : { createdAt: "DESC" }) as any,
      };
    });
  };

  // Tooltip text for the Rezeptprüfung pill — adapts per state so users know the
  // action, that closing the view never stops the run, and what the red badge means.
  const validationPillTooltip = validationMode
    ? validation.status === "running"
      ? "Schließen beendet nur die Ansicht – die Prüfung läuft im Hintergrund weiter"
      : "Schließen beendet nur die Ansicht – die Ergebnisse bleiben erhalten"
    : validation.status === "running"
      ? "Rezeptprüfung läuft im Hintergrund…"
      : validation.status === "done"
        ? `Rezeptprüfung abgeschlossen – ${validation.count} Rezepte mit Fehlern`
        : "Alle aktiven Rezepte auf Fehler prüfen";

  // ---- Toolbar -------------------------------------------------------------
  const toolbar = (
    <FlatCard
      title="Rezepte"
      sx={{ mb: 1 }}
      sxContent={{
        p: 1,
        // Non-chief has no validation nudge, so add a little top padding to
        // balance the larger bottom padding below.
        pt: isChief ? 1 : 1.5,
        position: "relative",
        overflow: "hidden",
        // MUI forces padding-bottom: 24px on the last child, making the toolbar
        // sit high; set our own value so the contents are visually centred.
        "&:last-child": { pb: 3 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          // Nudge the toolbar row up slightly without changing the card height.
          transform: isChief ? "translateY(-4px)" : "none",
          alignItems: "flex-end",
        }}
      >
        {/* Left filter group — nudged up slightly (transform only, no reflow → card keeps its size). */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "flex-end",
            transform: isChief ? "translateY(-6px)" : "none",
          }}
        >
        {/* Bearbeitungsstatus */}
        <Autocomplete
          size="small"
          disabled={validationMode}
          options={Object.values(RxDtoProcessingStatusEnum)}
          value={filters.processingStatus ?? null}
          onChange={(_, value) => setRxFilters({ processingStatus: value ?? undefined })}
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ color: getStatusColor(option) }}>
              {option}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Bearbeitungsstatus"
              margin="none"
              sx={{ minWidth: 200 }}
              InputProps={{
                ...params.InputProps,
                sx: { color: getStatusColor(filters.processingStatus) },
              }}
            />
          )}
          sx={{ minWidth: 200, backgroundColor: "transparent" }}
        />

      {/* Tarifgruppe */}
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography variant="caption" sx={{ color: validationMode ? "text.disabled" : "text.main" }}>Tarifgruppe</Typography>
        <Select
          size="small"
          disabled={validationMode}
          value={filters.tarifGroupId ?? ""}
          onChange={(e) =>
            setRxFilters({ tarifGroupId: (e.target.value as string) || undefined })
          }
          displayEmpty
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">
            <em>Alle</em>
          </MenuItem>
          {tarifGroupOptions.map((tg) => (
            <MenuItem key={tg.id} value={tg.id}>
              {tg.name}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Rx-Typ */}
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography variant="caption" sx={{ color: validationMode ? "text.disabled" : "text.main" }}>Rezept-Typ</Typography>
        <Select
          size="small"
          disabled={validationMode}
          value={filters.rxTypeId ?? ""}
          onChange={(e) =>
            setRxFilters({ rxTypeId: (e.target.value as string) || undefined })
          }
          displayEmpty
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">
            <em>Alle</em>
          </MenuItem>
          {rxTypeOptions.map((rt) => (
            <MenuItem key={rt.id} value={rt.id}>
              {rt.text}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Heilmittel (prescribed term).
          Autocomplete rather than a Select: the catalog is long, and matching
          runs through the app's own alias-aware helper so typing "Fango" or a
          BG position finds the term it stands for — the same matching the Rx
          entry field and the backend's own resolution use. */}
      <Autocomplete<PrescribedTherapyOptionDto>
        size="small"
        disabled={validationMode}
        options={prescribedTherapyOptions ?? []}
        value={selectedPrescribedTherapy}
        onChange={(_, option) => setRxFilters({ prescribedTherapyId: option?.id ?? undefined })}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option) => prescribedTherapyInputValue(option)}
        filterOptions={(options, { inputValue }) =>
          filterPrescribedTherapyOptions(options, inputValue)
        }
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.id}>
            {prescribedTherapyInputValue(option)}
            {option.label !== prescribedTherapyInputValue(option) && (
              <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                {option.label}
              </Typography>
            )}
          </Box>
        )}
        renderInput={(params) => (
          <TextField {...params} label="Heilmittel" margin="none" sx={{ minWidth: 200 }} />
        )}
        sx={{ minWidth: 200, backgroundColor: "transparent" }}
      />

      {/* Konto */}
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography variant="caption" sx={{ color: validationMode ? "text.disabled" : "text.main" }}>Konto</Typography>
        <Select
          size="small"
          disabled={validationMode}
          value={filters.accountId ?? ""}
          onChange={(e) =>
            setRxFilters({ accountId: (e.target.value as string) || undefined })
          }
          displayEmpty
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">
            <em>Alle</em>
          </MenuItem>
          {(accounts ?? []).map((acc) => (
            <MenuItem key={acc.id} value={acc.id}>
              {acc.name}
            </MenuItem>
          ))}
        </Select>
      </Box>

        <FormControlLabel
          control={
            <Switch
              disabled={validationMode}
              checked={!!filters.includeDeleted}
              onChange={(e) => setRxFilters({ includeDeleted: e.target.checked })}
            />
          }
          label="Gelöschte anzeigen"
        />
        </Box>

        {/* Push everything validation-related to the right of the divider. */}
        <Box sx={{ flexGrow: 1 }} />
        {/* All Rezeptprüfung UI (divider, count, Standort, pill, refresh) is chief-only. */}
        {isChief && (
        <>
        <Divider variant="middle" orientation="vertical" sx={{ height: 80, alignSelf: "flex-end", mb: -1.25 }} />

        {/* Validation group — bottom-aligned with the rest of the toolbar and generous spacing. */}
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 3, mb: 0.75, pr: 1.5 }}>
          {/* Live count (left-most of the validation group). */}
          {validationMode && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                alignSelf: "flex-end",
                pb: 1,
              }}
            >
              <Typography variant="subtitle1" sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                <span style={{ color: VALIDATION_COLORS.text }}>
                  {filteredValidationRows.length} mit Fehlern
                </span>
                {validation.status === "running" && (
                  <span style={{ color: "inherit", fontWeight: 400 }}>
                    {` · ${validation.processed}/${validation.total || "…"}`}
                  </span>
                )}
              </Typography>
            </Box>
          )}

          {/* Standort-Filter — selecting a location starts a new server-scoped
              run for it (deduped/replayed per location), so the user can inspect
              another Standort without changing their account location. Disabled
              while a run is in flight so a second run can't be started mid-run
              (which would race the in-flight subscription onto the wrong job). */}
          {validationMode && (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                variant="caption"
                sx={{ color: isValidationLoading ? "text.disabled" : "text.main" }}
              >
                Standort
              </Typography>
              <Tooltip
                title={isValidationLoading ? "Standort wechselbar, sobald die Prüfung abgeschlossen ist" : ""}
                disableInteractive
              >
                <span>
                  <Select
                    size="small"
                    disabled={isValidationLoading}
                    value={RegistryStore.validationLocationId ?? ""}
                    onChange={(e) => {
                      const nextLocation = (e.target.value as string) || undefined;
                      RegistryStore.setValidationLocationId(nextLocation);
                      void validation.start({ locationId: nextLocation, filters });
                    }}
                    displayEmpty
                    sx={{ minWidth: 180 }}
                  >
                    <MenuItem value="">
                      <em>Alle Standorte</em>
                    </MenuItem>
                    {(locations ?? []).map((loc) => (
                      <MenuItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </MenuItem>
                    ))}
                  </Select>
                </span>
              </Tooltip>
            </Box>
          )}

          {/* Rezeptprüfung mode toggle (pill — default when off, light red when on). Chief only. */}
          {isChief && (
          <Tooltip title={validationPillTooltip} disableInteractive>
          <Chip
            icon={
              validation.status === "running" ? (
                <CircularProgress size={16} color="inherit" disableShrink />
              ) : (
                <FactCheckIcon />
              )
            }
            label={
              validation.status === "done" && !validationMode ? (
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                  Rezeptprüfung
                  {/* Off-view only: in-view the count is already spelled out as "{n} mit Fehlern".
                      Red = Fehler, green = sauber. */}
                  <Box
                    component="span"
                    sx={{
                      ml: 0.75,
                      minWidth: 20,
                      height: 20,
                      px: 0.75,
                      borderRadius: 999,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      lineHeight: 1,
                      color: "#fff",
                      backgroundColor:
                        validation.count > 0 ? VALIDATION_COLORS.border : VALIDATION_COLORS.ok,
                    }}
                  >
                    {validation.count}
                  </Box>
                </Box>
              ) : (
                "Rezeptprüfung"
              )
            }
            variant={validationMode || validation.status === "running" ? "filled" : "outlined"}
            onClick={toggleValidationMode}
            onDelete={validationMode ? toggleValidationMode : undefined}
            sx={{
              borderRadius: 999,
              height: 40,
              px: 1,
              fontWeight: 600,
              fontSize: "0.95rem",
              "& .MuiChip-icon": { fontSize: "1.4rem" },
              "& .MuiChip-deleteIcon": { fontSize: "1.3rem" },
              "&:hover": { backgroundColor: VALIDATION_COLORS.bgHover },
              ...(validationMode || validation.status === "running"
                ? {
                    backgroundColor:
                      validation.status === "running"
                        ? VALIDATION_COLORS.bgRunning
                        : VALIDATION_COLORS.bgRest,
                    color: VALIDATION_COLORS.pillContent,
                    border: `1px solid ${VALIDATION_COLORS.border}`,
                    "& .MuiChip-icon": { color: VALIDATION_COLORS.pillContent },
                    "& .MuiChip-deleteIcon": {
                      color: VALIDATION_COLORS.pillContent,
                      "&:hover": { color: VALIDATION_COLORS.border },
                    },
                    "&:hover": { backgroundColor: VALIDATION_COLORS.bgActiveHover },
                  }
                : {}),
            }}
          />
          </Tooltip>
          )}

          {/* Refresh — only on the validation view once a run has finished. */}
          {isChief && validationMode && validation.status === "done" && (
            <Tooltip title="Erneut prüfen" disableInteractive>
              <IconButton
                aria-label="Rezeptprüfung erneut starten"
                // Must re-run the Standort that is selected — omitting it runs
                // EVERY location while the dropdown still shows one, which is
                // how other practices' Rxs turned up in a Klein-Hehlen run.
                // force: this is the one entry point that means "check the data
                // again", so it must not be served a replayed result (MD-184).
                onClick={() =>
                  void validation.start({
                    locationId: RegistryStore.validationLocationId,
                    filters,
                    force: true,
                  })
                }
                sx={{ ml: -2 }}
              >
                <RefreshIcon sx={{ fontSize: "1.8rem" }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        </>
        )}
      </Box>

      {/* Streaming progress bar flush along the bottom edge of the card. */}
      {isChief && validationMode && validation.status === "running" && (
        <LinearProgress
          variant={validation.total > 0 ? "determinate" : "indeterminate"}
          value={validation.progress}
          color="info"
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 4,
          }}
        />
      )}
    </FlatCard>
  );

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* Toolbar is lifted out of the tables and shared, so switching modes does
          not unmount/remount the toolbar (and its heavy MUI controls). */}
      {toolbar}

      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", position: "relative" }}>
        {/* The table is chosen by the DEFERRED mode so switching renders the new
            (heavy) table off the critical path; the previous table stays visible
            until the concurrent render is ready. */}
        {deferredValidationMode ? (
          <GenericInfiniteTable<RxValidationBulkRowDto>
            data={windowedValidationRows}
            columns={validationColumns}
            hasMoreData={
              validation.status === "running" ||
              validationRenderLimit < filteredValidationRows.length
            }
            fetchMoreData={() =>
              setValidationRenderLimit((n) => n + VALIDATION_PAGE_SIZE)
            }
            isLoading={validation.status === "running" && validation.rows.length === 0}
            onRowClick={handleRowClick}
            scrollId="registryRxValidationScroll"
            selectedIndex={RegistryStore.validationSelectedIndex}
            setSelectedIndex={RegistryStore.setValidationSelectedIndex}
            loadingMessage="Rezepte werden geprüft…"
            emptyMessage={
              validation.status === "error"
                ? "Beim Prüfen der Rezepte ist ein Fehler aufgetreten. Bitte erneut versuchen."
                : "Keine Rezepte mit Fehlern gefunden"
            }
          />
        ) : (
          <GenericInfiniteTable<RxTableDto>
            data={rxs}
            columns={columns}
            hasMoreData={!!hasMoreData}
            fetchMoreData={fetchNextPage}
            isLoading={isLoading}
            isFetchingNextPage={isFetchingNextPage}
            onRowClick={handleRowClick}
            scrollId="registryRxScroll"
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
            onSortChange={handleSortChange}
            sortBy={filters.sortBy as any}
            emptyMessage="Keine Rezepte gefunden"
          />
        )}
        {isSwitchingView && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.6)",
              zIndex: 1,
            }}
          >
            <CircularProgress size={32} sx={{ color: "#bdbdbd" }} />
          </Box>
        )}
      </Box>
    </Box>
  );
});

export default RegistryRxTab;
